import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileBadge2, Upload, ShieldCheck } from "lucide-react";
import { api, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";

const FIELDS = [
  ["im", "Inscrição municipal (São Paulo)", "text"],
  ["c_trib_nac", "Código de tributação nacional (6 dígitos)", "text"],
  ["c_trib_mun", "Código de tributação municipal (opcional)", "text"],
  ["c_nbs", "Código NBS (opcional)", "text"],
  ["p_aliq", "Alíquota de ISS (%)", "number"],
  ["c_loc_emi", "Código IBGE da cidade da empresa", "text"],
  ["c_loc_prest", "Código IBGE de onde o serviço é feito", "text"],
  ["serie", "Série da DPS", "text"],
  ["next_ndps", "Próximo número da DPS", "number"],
];

function CertificateBox({ cert, onUploaded }) {
  const [file, setFile] = useState(null);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("password", pwd);
    setBusy(true);
    try {
      await api.post("/nfse/certificate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificado salvo com segurança");
      setFile(null); setPwd(""); onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar certificado");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid="nfse-cert-box">
      {cert ? (
        <p className="text-xs text-emerald-700 flex items-start gap-1.5" data-testid="nfse-cert-info">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          <span>Certificado cadastrado{cert.cnpj ? ` (CNPJ ${cert.cnpj})` : ""} — válido até {formatDateBR(cert.not_after?.slice(0, 10))}</span>
        </p>
      ) : (
        <p className="text-xs text-amber-700" data-testid="nfse-cert-missing">Nenhum certificado cadastrado. Sem ele não dá para emitir.</p>
      )}
      <Label className="text-xs font-semibold text-slate-600">{cert ? "Trocar certificado A1 (.pfx)" : "Certificado A1 (.pfx ou .p12)"}</Label>
      <Input type="file" accept=".pfx,.p12,application/x-pkcs12" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="nfse-cert-file" />
      <PasswordInput value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Senha do certificado" data-testid="nfse-cert-password" />
      <Button size="sm" onClick={send} disabled={!file || !pwd || busy} className="w-full" data-testid="nfse-cert-upload">
        <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Enviando..." : "Salvar certificado"}
      </Button>
      <p className="text-[10.5px] text-slate-500">O arquivo e a senha ficam criptografados no servidor e nunca aparecem na tela.</p>
    </div>
  );
}

export default function NfseSettingsCard() {
  const [s, setS] = useState(null);
  const [cert, setCert] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/nfse/settings").then((r) => { setS(r.data.settings); setCert(r.data.certificate); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/nfse/settings", { ...s, p_aliq: Number(s.p_aliq) || 0, next_ndps: Number(s.next_ndps) || 1 });
      setS(r.data);
      toast.success("Dados fiscais salvos");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 card-shadow space-y-4" data-testid="nfse-settings-card">
      <div>
        <h2 className="font-heading font-black text-lg flex items-center gap-2"><FileBadge2 className="w-5 h-5 text-[#1e3a8a]" /> Nota fiscal (NFS-e Nacional)</h2>
        <p className="text-xs text-slate-500 mt-1">Simples Nacional (ME/EPP). Emissão em São Paulo, serviço em São Bernardo do Campo.</p>
      </div>
      <div className="grid grid-cols-2 gap-2" data-testid="nfse-ambiente">
        {[["restrita", "Testes (sem valor fiscal)"], ["producao", "Produção (nota real)"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setS({ ...s, ambiente: k })} data-testid={`nfse-ambiente-${k}`}
            className={`rounded-lg border-2 py-2 text-xs font-bold ${s.ambiente === k ? (k === "producao" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-800") : "border-slate-200 text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label, type]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">{label}</Label>
            <Input type={type} step="0.01" value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`nfse-field-${k}`} />
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#1e3a8a] hover:bg-[#1e40af] text-white" data-testid="nfse-settings-save">{saving ? "Salvando..." : "Salvar dados fiscais"}</Button>
      <CertificateBox cert={cert} onUploaded={load} />
    </section>
  );
}
